import { isJourneyProductType } from "./is-journey-product-type";
import type {
  TProductJourneyField,
  TProductJourneyFieldWidget,
} from "./journey-config.types";
import { EProductType } from "./products.type";

export type TJourneyFieldPresetCategory =
  | "personal"
  | "vehicle"
  | "credit"
  | "insurance"
  | "pet";

export type TJourneyFieldPresetDef = {
  /** Chave snake_case persistida no JSON (única por produto). */
  key: string;
  /** i18n: products.journey.preset.<presetKey> */
  presetKey: string;
  defaultWidget: TProductJourneyFieldWidget;
  integrationType?: string;
  defaultFillable: boolean;
  defaultRequired?: boolean;
  categories: TJourneyFieldPresetCategory[];
};

export const JOURNEY_FIELD_PRESETS: TJourneyFieldPresetDef[] = [
  {
    key: "client_full_name",
    presetKey: "client_full_name",
    defaultWidget: "text",
    integrationType: "person_full_name",
    defaultFillable: true,
    defaultRequired: true,
    categories: ["personal"],
  },
  {
    key: "client_email",
    presetKey: "client_email",
    defaultWidget: "email",
    integrationType: "email",
    defaultFillable: true,
    defaultRequired: true,
    categories: ["personal"],
  },
  {
    key: "client_phone",
    presetKey: "client_phone",
    defaultWidget: "tel",
    integrationType: "phone",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["personal", "credit", "vehicle"],
  },
  {
    key: "client_cpf",
    presetKey: "client_cpf",
    defaultWidget: "text",
    integrationType: "cpf",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["personal", "credit"],
  },
  {
    key: "birth_date",
    presetKey: "birth_date",
    defaultWidget: "date",
    integrationType: "birth_date",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["personal", "credit"],
  },
  {
    key: "requested_amount",
    presetKey: "requested_amount",
    defaultWidget: "currency",
    integrationType: "loan_amount",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["credit"],
  },
  {
    key: "loan_term_months",
    presetKey: "loan_term_months",
    defaultWidget: "number",
    integrationType: "loan_term_months",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["credit"],
  },
  {
    key: "vehicle_plate",
    presetKey: "vehicle_plate",
    defaultWidget: "text",
    integrationType: "vehicle_plate",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["vehicle"],
  },
  {
    key: "vehicle_chassis",
    presetKey: "vehicle_chassis",
    defaultWidget: "text",
    integrationType: "vehicle_chassis",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["vehicle"],
  },
  {
    key: "vehicle_model_year",
    presetKey: "vehicle_model_year",
    defaultWidget: "number",
    integrationType: "vehicle_model_year",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["vehicle"],
  },
  {
    key: "annual_mileage_km",
    presetKey: "annual_mileage_km",
    defaultWidget: "number",
    integrationType: "annual_mileage_km",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["vehicle"],
  },
  {
    key: "home_city",
    presetKey: "home_city",
    defaultWidget: "text",
    integrationType: "city",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["personal", "credit", "vehicle"],
  },
  {
    key: "quotation_notes",
    presetKey: "quotation_notes",
    defaultWidget: "textarea",
    integrationType: "free_text",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["personal", "credit", "vehicle"],
  },
  {
    key: "client_rg",
    presetKey: "client_rg",
    defaultWidget: "text",
    integrationType: "rg",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["personal", "credit", "insurance"],
  },
  {
    key: "home_state",
    presetKey: "home_state",
    defaultWidget: "text",
    integrationType: "state",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["personal", "credit", "vehicle", "insurance"],
  },
  {
    key: "home_zip",
    presetKey: "home_zip",
    defaultWidget: "text",
    integrationType: "zip_code",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["personal", "credit", "vehicle", "insurance"],
  },
  {
    key: "occupation",
    presetKey: "occupation",
    defaultWidget: "text",
    integrationType: "occupation",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["personal", "credit"],
  },
  {
    key: "monthly_income",
    presetKey: "monthly_income",
    defaultWidget: "currency",
    integrationType: "monthly_income",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["credit"],
  },
  {
    key: "vehicle_brand",
    presetKey: "vehicle_brand",
    defaultWidget: "text",
    integrationType: "vehicle_brand",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["vehicle", "insurance"],
  },
  {
    key: "vehicle_model",
    presetKey: "vehicle_model",
    defaultWidget: "text",
    integrationType: "vehicle_model",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["vehicle", "insurance"],
  },
  {
    key: "vehicle_color",
    presetKey: "vehicle_color",
    defaultWidget: "text",
    integrationType: "vehicle_color",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["vehicle"],
  },
  {
    key: "device_serial",
    presetKey: "device_serial",
    defaultWidget: "text",
    integrationType: "device_serial",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["insurance"],
  },
  {
    key: "insured_item_description",
    presetKey: "insured_item_description",
    defaultWidget: "textarea",
    integrationType: "insured_item_description",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["insurance"],
  },
  {
    key: "desired_coverage_amount",
    presetKey: "desired_coverage_amount",
    defaultWidget: "currency",
    integrationType: "coverage_amount",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["insurance"],
  },
  {
    key: "policy_start_date",
    presetKey: "policy_start_date",
    defaultWidget: "date",
    integrationType: "policy_start_date",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["insurance", "vehicle"],
  },
  {
    key: "pet_name",
    presetKey: "pet_name",
    defaultWidget: "text",
    integrationType: "pet_name",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["pet"],
  },
  {
    key: "pet_species",
    presetKey: "pet_species",
    defaultWidget: "text",
    integrationType: "pet_species",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["pet"],
  },
  {
    key: "pet_breed",
    presetKey: "pet_breed",
    defaultWidget: "text",
    integrationType: "pet_breed",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["pet"],
  },
  {
    key: "pet_size",
    presetKey: "pet_size",
    defaultWidget: "text",
    integrationType: "pet_size",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["pet"],
  },
  {
    key: "pet_age",
    presetKey: "pet_age",
    defaultWidget: "number",
    integrationType: "pet_age",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["pet"],
  },
  {
    key: "pet_registration_code",
    presetKey: "pet_registration_code",
    defaultWidget: "text",
    integrationType: "pet_registration_code",
    defaultFillable: true,
    defaultRequired: false,
    categories: ["pet"],
  },
];

const PRESET_KEYS = new Set(JOURNEY_FIELD_PRESETS.map((p) => p.key));

export function isPresetFieldKey(key: string): boolean {
  return PRESET_KEYS.has(key);
}

export function presetToField(def: TJourneyFieldPresetDef, label: string): TProductJourneyField {
  return {
    key: def.key,
    label,
    widget: def.defaultWidget,
    integrationType: def.integrationType,
    fillable: def.defaultFillable,
    required: def.defaultRequired ?? false,
  };
}

export function filterPresetsByProductType(
  productType: string | undefined | null,
): TJourneyFieldPresetDef[] {
  if (!productType) return JOURNEY_FIELD_PRESETS;
  if (productType === EProductType.CREDIT) {
    return JOURNEY_FIELD_PRESETS.filter((p) =>
      p.categories.includes("credit") || p.categories.includes("personal"),
    );
  }
  if (productType === EProductType.AUTO || productType === EProductType.MOTO) {
    return JOURNEY_FIELD_PRESETS.filter(
      (p) =>
        p.categories.includes("vehicle") ||
        p.categories.includes("personal"),
    );
  }
  if (
    productType === EProductType.WARRANTY_INSURANCE ||
    productType === EProductType.THEFT_INSURANCE
  ) {
    return JOURNEY_FIELD_PRESETS.filter(
      (p) =>
        p.categories.includes("insurance") ||
        p.categories.includes("personal") ||
        p.categories.includes("vehicle"),
    );
  }
  if (!isJourneyProductType(productType)) return JOURNEY_FIELD_PRESETS;
  return JOURNEY_FIELD_PRESETS;
}
